import { useRef } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import DownloadIcon from '@mui/icons-material/Download';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import html2canvas from 'html2canvas';

import type { ChartConfig } from '../../types';
import { CHART_COLORS } from '../../theme';

interface InlineChartProps {
  config: ChartConfig;
  onEnlarge: () => void;
}

export default function InlineChart({ config, onEnlarge }: InlineChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    if (!chartRef.current) return;

    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#1e293b',
        scale: 2,
      });
      const link = document.createElement('a');
      link.download = `chart-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to download chart:', error);
    }
  };

  const renderChart = () => {
    const { chartType, xAxis, yAxes, data } = config;

    const tooltipStyle = {
      backgroundColor: '#1e293b',
      border: '1px solid rgba(148, 163, 184, 0.2)',
      borderRadius: 8,
    };

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
              <XAxis
                dataKey={xAxis}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={{ stroke: '#94a3b8' }}
                tickLine={{ stroke: '#94a3b8' }}
                angle={-35}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={{ stroke: '#94a3b8' }}
                tickLine={{ stroke: '#94a3b8' }}
              />
              <RechartsTooltip contentStyle={tooltipStyle} />
              <Legend />
              {yAxes.map((yAxis, index) => (
                <Bar
                  key={yAxis}
                  dataKey={yAxis}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
              <XAxis
                dataKey={xAxis}
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={{ stroke: '#94a3b8' }}
                tickLine={{ stroke: '#94a3b8' }}
                angle={-35}
                textAnchor="end"
                height={60}
              />
              <YAxis
                tick={{ fill: '#94a3b8', fontSize: 11 }}
                axisLine={{ stroke: '#94a3b8' }}
                tickLine={{ stroke: '#94a3b8' }}
              />
              <RechartsTooltip contentStyle={tooltipStyle} />
              <Legend />
              {yAxes.map((yAxis, index) => (
                <Line
                  key={yAxis}
                  type="monotone"
                  dataKey={yAxis}
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth={2}
                  dot={{ fill: CHART_COLORS[index % CHART_COLORS.length], r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
              <Pie
                data={data}
                dataKey={yAxes[0]}
                nameKey={xAxis}
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) =>
                  `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
                }
                labelLine={{ stroke: '#94a3b8' }}
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <RechartsTooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        bgcolor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          gap: 0.5,
          zIndex: 10,
        }}
      >
        <Tooltip title="Enlarge">
          <IconButton
            size="small"
            onClick={onEnlarge}
            sx={{
              bgcolor: 'rgba(139, 92, 246, 0.2)',
              '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.4)' },
            }}
          >
            <OpenInFullIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download PNG">
          <IconButton
            size="small"
            onClick={handleDownload}
            sx={{
              bgcolor: 'rgba(139, 92, 246, 0.2)',
              '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.4)' },
            }}
          >
            <DownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Chart */}
      <Box ref={chartRef} sx={{ pt: 1 }}>
        {config.title && (
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', mb: 1, color: 'text.secondary' }}
          >
            {config.title}
          </Typography>
        )}
        {renderChart()}
      </Box>
    </Box>
  );
}
